import * as React from "react";
import { Box, Typography } from "@mui/material";
import { JSX, useState } from "react";
import { useSelector } from "react-redux";
import Filters from "./Filters";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import * as calFns from "../../components/Calendar/functions/calendarFunctions";
import * as reportsApi from "../../api/reports";
import "./Reports.scss";
import useMobileScreen from "../../hooks/useMobileScreen";
import Can from "../Policy/Can";
import { notificationAdd } from "../../actions/notification";
import { commonErrorMessage } from "../../helpers/messages";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import { ServiceProviderStatisticsReportData } from "../../interfaces/AssessmentInterfaces";
import { DataType } from "../../components/OList/ListInterface";
import { spFullNameSelector } from "../../reducers/serviceProvider";
import { downloadExcel } from "../../helpers/files";
import { formatDataForExcel, formatHeaderForExcel } from "../accounts/Reports/helper";
import { tl, t } from "../../components/translate";
import { useAppDispatch } from "../../store/hooks";
import useDepartmentSubscription from "../../hooks/useDepartmentSubscription";
import useGetDepartments from "../../hooks/department";
import { Department } from "../../interfaces/DepartmentInterface";
import { RootState } from "../../store";

const docColumns = () => [
  t("reports.serviceProvider"),
  t("registrationNo"),
  t("reports.department"),
  t("reports.newClients"),
  t("reports.oldClients"),
  t("reports.followupClients"),
  t("reports.totalClients")
];

const docRowProcessor = ({
  name,
  department,
  registrationNumber,
  totalClients,
  followUpClient,
  oldClient,
  newClient,
  // eslint-disable-next-line camelcase
  __meta__row_type
}: ServiceProviderStatisticsReportData) => {
  // eslint-disable-next-line camelcase
  if (__meta__row_type === "segment_summary") return undefined;
  return [name, registrationNumber, department, newClient, oldClient, followUpClient, totalClients];
};

const processData = (data: ServiceProviderStatisticsReportData[], departments: Department[]) =>
  data.map((item) => ({
    ...item,
    name: spFullNameSelector(item.serviceProvider),
    department:
      departments.find((department) => department.id === item.serviceProvider?.departmentId)
        ?.name || "",
    registrationNumber: item.serviceProvider?.registrationNumber || ""
  }));

const ServiceProviderStatisticsReport = (): JSX.Element => {
  const dispatch = useAppDispatch();

  // hooks
  const departments = useGetDepartments();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();
  const { isDepartmentSubscribed } = useDepartmentSubscription();

  // selectors
  const serviceProviderId = useSelector((state: RootState) =>
    state.userContext.userCreds.authenticable === "serviceProvider"
      ? state.userContext.userCreds.authenticableId
      : null
  );

  // local states
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [reportData, setReportData] = React.useState<ServiceProviderStatisticsReportData[]>([]);
  const [filters, setFilters] = React.useState({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    serviceProviderIds: serviceProviderId ? [serviceProviderId] : [],
    ...(isDepartmentSubscribed ? { departmentId: null } : {})
  });

  // effects
  React.useEffect(() => {
    (async () => {
      try {
        setIsRequestLoading(true);
        const result = await reportsApi.serviceProviderStatisticsReport(filters);
        setReportData(processData(result, departments));
      } catch (error: any) {
        notificationAdd({
          id: new Date().getTime(),
          message: error?.message || error?.data?.message || commonErrorMessage,
          autoTimeout: true,
          variant: "error"
        });
      } finally {
        setIsRequestLoading(false);
      }
    })();
  }, [filters]);

  return (
    <Can policyAccessKey="report:patientFlowReport">
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* filter */}
        <Box sx={{ px: 3, py: 2 }}>
          <Filters
            filters={filters}
            onSetFilters={(updatedFilters) => setFilters(updatedFilters)}
            isRequestLoading={isRequestLoading}
          />
        </Box>

        {/* list */}
        <Box
          className="serviceProviderStatisticsReportList"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            width: "100%",
            overflow: "auto"
          }}
        >
          <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1100px" : "auto" }}>
            <List<DataType & ServiceProviderStatisticsReportData>
              automation="patientFlowReportList"
              data={reportData}
              rowHeight={50}
              defaultSortColumn="date"
              defaultSortOrder={-1}
              adjustHeightToContents
              columns={[
                {
                  key: "name",
                  label: "ServiceProvider name",
                  sortable: true,
                  formatter: ({ name }) => <Typography>{name}</Typography>
                },
                {
                  key: "registrationNumber",
                  label: "Registration Number",
                  sortable: true,
                  formatter: ({ registrationNumber }) => (
                    <Typography>{registrationNumber || ""}</Typography>
                  )
                },
                {
                  key: "department",
                  label: "Department",
                  sortable: true,
                  formatter: ({ department }) => <Typography>{department}</Typography>
                },
                {
                  key: "newClient",
                  label: "New Client",
                  formatter: (row) => <Typography>{row.newClient}</Typography>
                },
                {
                  key: "oldClient",
                  label: "Old Client",
                  formatter: (row) => <Typography>{row.oldClient}</Typography>
                },
                {
                  key: "followUpClient",
                  label: "Follow up Client",
                  formatter: (row) => <Typography>{row.followUpClient}</Typography>
                },
                {
                  key: "totalClients",
                  label: "Total Clients",
                  formatter: ({ totalClients }) => <Typography>{totalClients}</Typography>
                }
              ]}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  No items to show.
                </Box>
              </EmptyView>
              <ListActions>
                {({ getProcessedData }) => (
                  <Menu>
                    <MenuItem
                      onClick={async () => {
                        try {
                          await downloadExcel(
                            "ServiceProviderStatisticsReport",
                            formatHeaderForExcel(docColumns()),
                            formatDataForExcel(getProcessedData(), docRowProcessor)
                          );
                        } catch (err) {
                          dispatch(
                            notificationAdd({
                              id: new Date().getUTCMilliseconds(),
                              variant: "error",
                              message: "Failed to download Excel.",
                              autoTimeout: true
                            })
                          );
                        }
                      }}
                    >
                      {tl("reports.excel")}
                    </MenuItem>
                  </Menu>
                )}
              </ListActions>
            </List>

            <Box className="totalBar">
              <Typography className="total-bar-title">{t("reports.total")}</Typography>
              <Box className="total-bar-blank" />
              <Box className="total-bar-blank" />
              <Typography className="total-bar-newClient">
                {reportData.reduce((acc, cur) => acc + cur.newClient || 0, 0)}
              </Typography>
              <Typography className="total-bar-oldClient">
                {reportData.reduce((acc, cur) => acc + cur.oldClient || 0, 0)}
              </Typography>
              <Typography className="total-bar-followUpClient">
                {reportData.reduce((acc, cur) => acc + cur.followUpClient || 0, 0)}
              </Typography>
              <Typography className="total-bar-totalClients">
                {reportData.reduce((acc, cur) => acc + cur.totalClients || 0, 0)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Can>
  );
};

export default ServiceProviderStatisticsReport;
